/**
 * auth-handlers.ts - Event handlers for authentication forms
 */

import type { LoginErrorResponse } from '../../types/auth';
import * as authAPI from './auth-api';
import { setCurrentUser } from './auth-state';
import { updateAuthUI } from './auth-ui';
import { closeAuthModal } from './auth-modals';

/**
 * Claim any demo tournaments from sessionStorage and transfer to user account
 */
async function claimDemoTournaments(): Promise<void> {
  const demoTournamentIds: string[] = JSON.parse(sessionStorage.getItem('demoTournaments') || '[]');

  if (demoTournamentIds.length === 0) return;

  try {
    const response = await fetch('/api/tournaments/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ tournamentIds: demoTournamentIds })
    });

    if (response.ok) {
      // Clear sessionStorage after successful claim
      sessionStorage.removeItem('demoTournaments');
      console.log('[Auth] Demo tournaments claimed successfully');
    }
  } catch (error) {
    console.error('[Auth] Error claiming demo tournaments:', error);
  }
}

/**
 * Handle login form submission
 * @param event - Form submit event
 */
export async function handleLogin(event: Event): Promise<void> {
  event.preventDefault();

  const emailInput = document.getElementById('login-email') as HTMLInputElement;
  const passwordInput = document.getElementById('login-password') as HTMLInputElement;
  const errorDiv = document.getElementById('login-error');

  if (!emailInput || !passwordInput || !errorDiv) return;

  const email = emailInput.value;
  const password = passwordInput.value;

  try {
    const data = await authAPI.login({ email, password });
    setCurrentUser(data.user);

    // Close modal and update UI FIRST, before any other async operations
    // This ensures the UI is responsive even if server restarts
    closeAuthModal();
    updateAuthUI(data.user);

    // Claim demo tournaments in background (non-blocking)
    // Don't await - if server restarts, we don't want to block the UI
    claimDemoTournaments().catch(err => {
      console.warn('[Auth] Failed to claim demo tournaments:', err);
    });

    // Reload tournaments to show user-specific ones
    if (window.loadTournamentsList) {
      window.loadTournamentsList();
    }
  } catch (error) {
    const loginError = error as LoginErrorResponse;
    if (loginError.requiresVerification && loginError.email) {
      // Email not verified
      errorDiv.innerHTML = `
        ${loginError.error}<br>
        <button onclick="resendVerificationFromLogin('${loginError.email}')" style="margin-top: 10px; padding: 5px 10px; cursor: pointer;">
          Resend Verification Email
        </button>
      `;
    } else {
      errorDiv.textContent = loginError.error || 'Login failed';
    }
  }
}

/**
 * Handle register form submission
 * @param event - Form submit event
 */
export async function handleRegister(event: Event): Promise<void> {
  event.preventDefault();

  const usernameInput = document.getElementById('register-username') as HTMLInputElement;
  const emailInput = document.getElementById('register-email') as HTMLInputElement;
  const passwordInput = document.getElementById('register-password') as HTMLInputElement;
  const passwordConfirmInput = document.getElementById('register-password-confirm') as HTMLInputElement;
  const errorDiv = document.getElementById('register-error');
  const successDiv = document.getElementById('register-success');

  if (!usernameInput || !emailInput || !passwordInput || !passwordConfirmInput || !errorDiv) return;

  const username = usernameInput.value;
  const email = emailInput.value;
  const password = passwordInput.value;
  const passwordConfirm = passwordConfirmInput.value;

  // Clear previous messages
  errorDiv.textContent = '';
  if (successDiv) successDiv.textContent = '';

  // Client-side validation
  if (password !== passwordConfirm) {
    errorDiv.textContent = window.i18n ? window.i18n.t('auth.passwordsDoNotMatch') : 'Passwords do not match';
    return;
  }

  try {
    const data = await authAPI.register({ username, email, password });

    if (data.requiresVerification) {
      // Email verification required - show success message and hide all wizard steps
      const step1 = document.getElementById('register-step-1');
      const step2SSO = document.getElementById('register-step-2-sso');
      const step2Credentials = document.getElementById('register-step-2-credentials');
      const step3Password = document.getElementById('register-step-3-password');

      if (step1) step1.style.display = 'none';
      if (step2SSO) step2SSO.style.display = 'none';
      if (step2Credentials) step2Credentials.style.display = 'none';
      if (step3Password) step3Password.style.display = 'none';

      // Track registration conversion for Google Ads
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', {
          'send_to': 'AW-17821418503/Kk2gCNnqs9UbEIeI9bFC'
        });
      }

      if (successDiv) {
        const registrationTitle = window.i18n ? window.i18n.t('auth.registrationSuccessful') : 'Registration Successful!';
        const spamFolderMsg = window.i18n ? window.i18n.t('auth.checkSpamFolder') : 'Check your spam folder if you don\'t see the email.';
        const closeButton = window.i18n ? window.i18n.t('common.close') : 'Close';

        successDiv.innerHTML = `
          <div style="text-align: center; padding: 20px;">
            <div style="font-size: 48px; margin-bottom: 15px;">✉️</div>
            <h3 style="margin: 0 0 10px 0; color: #155724;">${registrationTitle}</h3>
            <p style="margin: 10px 0;">${data.message}</p>
            <p style="font-size: 14px; color: #666;">${spamFolderMsg}</p>
            <button onclick="closeAuthModal()" style="margin-top: 20px; padding: 10px 20px; background-color: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer;">
              ${closeButton}
            </button>
          </div>
        `;
        successDiv.style.display = 'block';
      }
    } else if (data.user) {
      // Old flow - direct login (fallback)
      setCurrentUser(data.user);

      // Close modal and update UI FIRST
      closeAuthModal();
      updateAuthUI(data.user);

      // Claim demo tournaments in background (non-blocking)
      claimDemoTournaments().catch(err => {
        console.warn('[Auth] Failed to claim demo tournaments:', err);
      });

      if (window.loadTournamentsList) {
        window.loadTournamentsList();
      }
    }
  } catch (error) {
    errorDiv.textContent = (error as Error).message || 'Registration failed';
  }
}

/**
 * Handle logout
 */
export async function handleLogout(): Promise<void> {
  try {
    await authAPI.logout();
    setCurrentUser(null);

    // Redirect to landing page if on mytournaments, tournament detail, profile, or admin pages
    const currentPath = window.location.pathname;
    if (currentPath.includes('/mytournaments') ||
        currentPath.includes('/tournament.html') ||
        currentPath.includes('/profile') ||
        currentPath.includes('/admin')) {
      window.location.href = '/';
      return;
    }

    // Update UI for other pages
    updateAuthUI(null);

    if (window.loadTournamentsList) {
      window.loadTournamentsList();
    }
  } catch (error) {
    console.error('Logout error:', error);
  }
}

/**
 * Handle password change form submission
 * @param event - Form submit event
 */
export async function handlePasswordChange(event: Event): Promise<void> {
  event.preventDefault();

  const currentPasswordInput = document.getElementById('current-password') as HTMLInputElement;
  const newPasswordInput = document.getElementById('new-password') as HTMLInputElement;
  const newPasswordConfirmInput = document.getElementById('new-password-confirm') as HTMLInputElement;
  const errorDiv = document.getElementById('password-error');
  const successDiv = document.getElementById('password-success');

  if (!currentPasswordInput || !newPasswordInput || !newPasswordConfirmInput || !errorDiv || !successDiv) return;

  const currentPassword = currentPasswordInput.value;
  const newPassword = newPasswordInput.value;
  const newPasswordConfirm = newPasswordConfirmInput.value;

  // Clear messages
  errorDiv.textContent = '';
  successDiv.textContent = '';

  // Client-side validation
  if (newPassword !== newPasswordConfirm) {
    errorDiv.textContent = window.i18n ? window.i18n.t('auth.newPasswordsDoNotMatch') : 'New passwords do not match';
    return;
  }

  if (newPassword.length < 8) {
    errorDiv.textContent = window.i18n ? window.i18n.t('auth.passwordTooShort') : 'New password must be at least 8 characters';
    return;
  }

  try {
    await authAPI.changePassword({ currentPassword, newPassword });

    // Clear the form and show success state
    currentPasswordInput.value = '';
    newPasswordInput.value = '';
    newPasswordConfirmInput.value = '';

    // Visual feedback: briefly show checkmark on submit button
    const submitBtn = document.querySelector('#password-form button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) {
      const originalText = submitBtn.textContent;
      submitBtn.textContent = '✓';
      submitBtn.style.backgroundColor = '#28a745';
      setTimeout(() => {
        submitBtn.textContent = originalText;
        submitBtn.style.backgroundColor = '';
      }, 2000);
    }
  } catch (error) {
    errorDiv.textContent = (error as Error).message || (window.i18n ? window.i18n.t('resetPassword.error') : 'Failed to update password');
  }
}

/**
 * Handle forgot password form submission
 * @param event - Form submit event
 */
export async function handleForgotPassword(event: Event): Promise<void> {
  event.preventDefault();

  const emailInput = document.getElementById('forgot-password-email') as HTMLInputElement;
  const errorDiv = document.getElementById('forgot-password-error');
  const successDiv = document.getElementById('forgot-password-success');

  if (!emailInput || !errorDiv || !successDiv) return;

  const email = emailInput.value;

  errorDiv.textContent = '';
  successDiv.textContent = '';

  try {
    await authAPI.forgotPassword(email);
    emailInput.value = '';

    // Show inline confirmation
    const submitBtn = document.querySelector('#forgot-password-form button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) {
      const originalText = submitBtn.textContent;
      submitBtn.textContent = '✓ Email sent';
      submitBtn.style.backgroundColor = '#28a745';
      setTimeout(() => {
        submitBtn.textContent = originalText;
        submitBtn.style.backgroundColor = '';
      }, 3000);
    }
  } catch (error) {
    errorDiv.textContent = (error as Error).message || 'Failed to send reset email';
  }
}

/**
 * Handle contact form submission
 * @param event - Form submit event
 */
export async function handleContactForm(event: Event): Promise<void> {
  event.preventDefault();

  const nameInput = document.getElementById('contact-name') as HTMLInputElement;
  const emailInput = document.getElementById('contact-email') as HTMLInputElement;
  const subjectInput = document.getElementById('contact-subject') as HTMLInputElement;
  const messageInput = document.getElementById('contact-message') as HTMLTextAreaElement;
  const errorDiv = document.getElementById('contact-error');
  const successDiv = document.getElementById('contact-success');

  if (!nameInput || !emailInput || !subjectInput || !messageInput || !errorDiv || !successDiv) return;

  const name = nameInput.value;
  const email = emailInput.value;
  const subject = subjectInput.value;
  const message = messageInput.value;

  errorDiv.textContent = '';
  successDiv.textContent = '';

  try {
    await authAPI.sendContactForm({ name, email, subject, message });

    // Clear form fields
    nameInput.value = '';
    emailInput.value = '';
    subjectInput.value = '';
    messageInput.value = '';

    // Show success message
    const successTitle = window.i18n ? window.i18n.t('contact.successTitle') : 'Message Sent!';
    const successMessage = window.i18n ? window.i18n.t('contact.successMessage') : 'Thank you for your message. We\'ll get back to you soon.';
    const closeButton = window.i18n ? window.i18n.t('common.close') : 'Close';

    successDiv.innerHTML = `
      <div style="text-align: center; padding: 20px;">
        <div style="font-size: 48px; margin-bottom: 15px;">✉️</div>
        <h3 style="margin: 0 0 10px 0; color: #155724;">${successTitle}</h3>
        <p style="margin: 10px 0;">${successMessage}</p>
        <button onclick="closeContactModal()" style="margin-top: 20px; padding: 10px 20px; background-color: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer;">
          ${closeButton}
        </button>
      </div>
    `;
    successDiv.style.display = 'block';

    // Hide the form fields
    const form = document.querySelector('#contact-modal form') as HTMLFormElement;
    if (form) {
      const formGroups = form.querySelectorAll('.form-group, .modal-actions');
      formGroups.forEach((el) => {
        (el as HTMLElement).style.display = 'none';
      });
    }
  } catch (error) {
    errorDiv.textContent = (error as Error).message || (window.i18n ? window.i18n.t('contact.error') : 'Failed to send message');
  }
}

/**
 * Resend verification email from login screen
 * @param email - User's email address
 */
export async function resendVerificationFromLogin(email: string): Promise<void> {
  const button = document.querySelector(`button[onclick*="resendVerificationFromLogin"]`) as HTMLButtonElement;

  try {
    await authAPI.resendVerification(email);

    // Visual feedback on button
    if (button) {
      const originalText = button.textContent;
      button.textContent = '✓ Email sent';
      button.style.backgroundColor = '#28a745';
      button.style.color = 'white';
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
        button.style.color = '';
      }, 3000);
    }
  } catch (error) {
    const errorMsg = window.i18n ? window.i18n.t('auth.verificationEmailFailed') : 'Failed to send verification email';
    alert((error as Error).message || errorMsg);
  }
}

/**
 * Start editing username - show the edit form
 */
export function startEditUsername(): void {
  const usernameDisplay = document.getElementById('account-username');
  const editBtn = document.getElementById('edit-username-btn');
  const editForm = document.getElementById('username-edit-form');
  const editInput = document.getElementById('edit-username-input') as HTMLInputElement;
  const errorDiv = document.getElementById('username-error');
  const successDiv = document.getElementById('username-success');

  if (!usernameDisplay || !editBtn || !editForm || !editInput) return;

  // Clear messages
  if (errorDiv) errorDiv.textContent = '';
  if (successDiv) successDiv.textContent = '';

  // Set current username in input
  editInput.value = usernameDisplay.textContent || '';

  // Hide display, show form
  usernameDisplay.parentElement?.classList.add('editing');
  editForm.classList.remove('hidden');
  editInput.focus();
  editInput.select();
}

/**
 * Cancel editing username - hide the edit form
 */
export function cancelEditUsername(): void {
  const usernameDisplay = document.getElementById('account-username');
  const editForm = document.getElementById('username-edit-form');
  const errorDiv = document.getElementById('username-error');
  const successDiv = document.getElementById('username-success');

  if (!editForm) return;

  // Clear messages
  if (errorDiv) errorDiv.textContent = '';
  if (successDiv) successDiv.textContent = '';

  // Show display, hide form
  usernameDisplay?.parentElement?.classList.remove('editing');
  editForm.classList.add('hidden');
}

/**
 * Save the new username
 */
export async function saveUsername(): Promise<void> {
  const usernameDisplay = document.getElementById('account-username');
  const editInput = document.getElementById('edit-username-input') as HTMLInputElement;
  const editForm = document.getElementById('username-edit-form');
  const errorDiv = document.getElementById('username-error');
  const successDiv = document.getElementById('username-success');

  if (!editInput || !errorDiv || !successDiv) return;

  const newUsername = editInput.value.trim();

  // Clear previous messages
  errorDiv.textContent = '';
  successDiv.textContent = '';

  // Client-side validation
  if (!newUsername) {
    errorDiv.textContent = window.i18n ? window.i18n.t('auth.usernameRequired') : 'Username is required';
    return;
  }

  if (newUsername.length < 3) {
    errorDiv.textContent = window.i18n ? window.i18n.t('auth.usernameTooShort') : 'Username must be at least 3 characters';
    return;
  }

  if (newUsername.length > 50) {
    errorDiv.textContent = window.i18n ? window.i18n.t('auth.usernameTooLong') : 'Username must be less than 50 characters';
    return;
  }

  try {
    const data = await authAPI.updateUsername(newUsername);

    // Update the current user state
    setCurrentUser(data.user);

    // Update display
    if (usernameDisplay) {
      usernameDisplay.textContent = newUsername;
    }

    // Update the UI (username in header, etc.)
    updateAuthUI(data.user);

    // Hide form
    usernameDisplay?.parentElement?.classList.remove('editing');
    if (editForm) editForm.classList.add('hidden');

    // Brief success indication
    successDiv.textContent = window.i18n ? window.i18n.t('account.usernameUpdated') : 'Username updated!';
    setTimeout(() => {
      successDiv.textContent = '';
    }, 2000);
  } catch (error) {
    errorDiv.textContent = (error as Error).message || (window.i18n ? window.i18n.t('account.usernameUpdateError') : 'Failed to update username');
  }
}

// Export to window for onclick handlers

window.handleLogin = handleLogin;
window.handleRegister = handleRegister;
window.handleLogout = handleLogout;
window.handlePasswordChange = handlePasswordChange;
window.handleForgotPassword = handleForgotPassword;
window.handleContactForm = handleContactForm;
window.resendVerificationFromLogin = resendVerificationFromLogin;
window.startEditUsername = startEditUsername;
window.cancelEditUsername = cancelEditUsername;
window.saveUsername = saveUsername;
